import { PropsWithChildren } from "react";
import { useAppChrome } from "@/common/chrome";
import { BrandLink } from "./BrandMark";
import CurrentIntervalWidget from "./CurrentIntervalWidget";
import CurrentPageWidget from "./CurrentPageWidget";
import RecordOwnerWidget from "./RecordOwnerWidget";

export function HeaderText({ children }: PropsWithChildren) {
  return (
    <span
      className="min-w-0 truncate text-[16px] font-semibold text-foreground"
      style={{ fontFamily: "var(--font-serif)" }}
    >
      {children}
    </span>
  );
}

/**
 * Left inset of the header's time button, chosen so the brand mark's centre
 * lands on the timeline's spine: 1px of the list's border, plus SPINE_X (24)
 * from `RecordsTimeline`, less the button's own px-2 and half the 20px mark.
 */
const BRAND_PL = 1 + 24 - 8 - 10;

/**
 * Two slots, one per sidebar: the time control on the left, the space on the
 * right.
 *
 * Where a sidebar is not mounted at all — see `useAppChrome` — its slot keeps
 * its place but changes what it holds. The left falls back to the brand, which
 * otherwise lives in the time panel's own header and would be nowhere on screen
 * without it; that also leaves a way back to "/" from a page with no panels.
 * The right names the record's owner on a record page, since there is no
 * collection list behind it to open.
 *
 * The header does not say which page this is. Every page that has a title worth
 * reading shows it in its own body — a collection names itself in `RecordsBand`,
 * above the records it heads — and repeating it here only put the name somewhere
 * it could not be acted on.
 */
export default function Header() {
  const chrome = useAppChrome();

  return (
    <header className="bg-card sticky top-0 z-50 border-b min-h-[55px]">
      <div
        className="h-full flex items-center justify-between py-2 pr-2 gap-2"
        style={{ paddingLeft: BRAND_PL }}
      >
        {/* Kept as a slot even when it renders nothing, so justify-between
            still holds the right-hand control to the edge. */}
        <div className="flex items-center min-w-0">
          {chrome.showTimeSidebar ? (
            <CurrentIntervalWidget showInterval={chrome.showInterval} />
          ) : (
            <BrandLink />
          )}
        </div>
        {chrome.isRecordPage ? (
          <RecordOwnerWidget
            userRef={chrome.recordUserRef}
            recordID={chrome.recordID}
          />
        ) : (
          <CurrentPageWidget />
        )}
      </div>
    </header>
  );
}
